<template>
    <div class="relative w-full">
        <div
            @click="toggleModal"
            :class="[datepickerClass, { 'opacity-50 bg-gray-200': disabled }]"
        >
            <div class="flex flex-col">
                <label v-if="label" :class="labelClass">{{ label }}</label>
                <span :class="displayedLabelClass">
                    {{ displayedDate }}
                </span>
            </div>
            <span v-if="iconBack" class="ml-2">
                <AppIcon :icon="iconBack" class="w-5 h-5 text-gray-600" />
            </span>
        </div>

        <!-- Modal: dialog (teleport ke body) atau dropdown absolute -->
        <teleport to="body" :disabled="!dialog">
            <transition name="slide-down">
                <div
                    v-if="isModalOpen"
                    :class="
                        dialog
                            ? 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4'
                            : ['absolute mt-2 z-50 w-full', positionClass]
                    "
                >
                    <div
                        @click.stop
                        :class="
                            dialog
                                ? 'relative w-[min(400px,92vw)] max-h-[85vh] overflow-auto bg-white shadow-2xl rounded-xl'
                                : ''
                        "
                    >
                        <BaseDate
                            :selectedDate="normalizedRange"
                            :range="true"
                            :position="position"
                            :disablePastDates="disablePastDates"
                            @dateSelected="handleDateSelected"
                            @close="closeModal"
                            :showPrice="showPrice"
                            :class="
                                dialog
                                    ? '!static !mt-0 w-full !max-w-none !shadow-none'
                                    : ''
                            "
                            :style="
                                dialog ? '' : 'width: 400px; overflow-x: auto'
                            "
                        />
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
import BaseDate from "./BaseDate.vue";
import { format } from "date-fns";

export default {
    name: "BaseDateRangePicker",
    components: { BaseDate },
    props: {
        label: { type: String, default: "" },
        labelClass: {
            type: String,
            default: "text-sm text-blue-500 font-medium",
        },
        datepickerClass: {
            type: String,
            default:
                "flex items-center justify-between border border-blue-500 rounded-lg px-3 py-2 cursor-pointer",
        },
        placeholder: { type: String, default: "Select date" },
        iconBack: { type: String, default: "mdi:calendar" },
        modelValue: {
            type: Object,
            default: () => ({
                startDate: null,
                endDate: null,
            }),
        },
        position: {
            type: String,
            default: "right",
            validator: (v) => ["left", "right"].includes(v),
        },
        disabled: { type: Boolean, default: false },
        disablePastDates: { type: Boolean, default: true },
        // ponytail: dialog dipakai saat trigger ada di panel sempit/scrollable
        // (sidebar filter mobile) — dropdown absolute ikut ter-clip di sana.
        dialog: { type: Boolean, default: false },
        showPrice: { type: Boolean, default: true },
    },
    data() {
        return { isModalOpen: false };
    },
    computed: {
        // Normalisasi range supaya selalu Date valid atau null
        normalizedRange() {
            return {
                startDate: this.normalizeDate(this.modelValue?.startDate),
                endDate: this.normalizeDate(this.modelValue?.endDate),
            };
        },
        displayedDate() {
            const s = this.normalizedRange.startDate;
            const e = this.normalizedRange.endDate;
            if (s && e) {
                return `${format(s, "dd MMM yyyy")} - ${format(
                    e,
                    "dd MMM yyyy"
                )}`;
            }
            return this.placeholder;
        },
        displayedLabelClass() {
            const s = this.normalizedRange.startDate;
            const e = this.normalizedRange.endDate;
            return s && e ? "text-sm text-gray-700" : "text-sm text-gray-500";
        },
        positionClass() {
            return this.position === "right"
                ? "sm:left-auto sm:right-0"
                : "sm:left-0 sm:right-auto";
        },
    },
    watch: {
        // Jika parent mengirim nilai tidak valid, kita rapikan & fallback ke default
        modelValue: {
            deep: true,
            immediate: false,
            handler(v) {
                const s = this.normalizeDate(v?.startDate);
                const e = this.normalizeDate(v?.endDate);
                if (!s || !e) {
                    // fallback: today & tomorrow
                    const today = this.today();
                    const tomorrow = this.tomorrow(today);
                    this.$emit("update:modelValue", {
                        startDate: today,
                        endDate: tomorrow,
                    });
                }
            },
        },
    },
    methods: {
        toggleModal() {
            if (this.disabled) return;
            this.isModalOpen = !this.isModalOpen;

            // Saat dibuka dan tanggal invalid -> set default hari ini & besok
            if (this.isModalOpen) {
                const s = this.normalizedRange.startDate;
                const e = this.normalizedRange.endDate;
                if (!s || !e) {
                    const today = this.today();
                    const tomorrow = this.tomorrow(today);
                    this.$emit("update:modelValue", {
                        startDate: today,
                        endDate: tomorrow,
                    });
                }
            }
        },
        handleDateSelected(selectedDate) {
            // selectedDate: { startDate, endDate }
            const s = this.normalizeDate(selectedDate?.startDate);
            const e = this.normalizeDate(selectedDate?.endDate);
            const out = {
                startDate: s || this.today(),
                endDate: e || this.tomorrow(),
            };
            this.$emit("update:modelValue", out);
            this.isModalOpen = false;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        handleClickOutside(event) {
            if (this.isModalOpen && !this.$el.contains(event.target)) {
                this.isModalOpen = false;
            }
        },

        // ===== Helpers =====
        normalizeDate(v) {
            if (!v && v !== 0) return null;
            const d = v instanceof Date ? v : new Date(v);
            return isNaN(d) ? null : d;
        },
        today() {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return d;
        },
        tomorrow(from = null) {
            const base = from ? new Date(from) : this.today();
            const t = new Date(base);
            t.setDate(base.getDate() + 1);
            return t;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);

        // Inisialisasi awal bila kosong/invalid
        const s = this.normalizedRange.startDate;
        const e = this.normalizedRange.endDate;
        if (!s || !e) {
            const today = this.today();
            const tomorrow = this.tomorrow(today);
            this.$emit("update:modelValue", {
                startDate: today,
                endDate: tomorrow,
            });
        }
    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
    },
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
.z-50 {
    z-index: 50;
}
.opacity-50 {
    opacity: 0.5;
}
.bg-gray-200 {
    background-color: #e5e7eb;
}
</style>
